import { useTheme } from "@mui/material";
import Typography from "components/basic/typography";

/** A single label/value row. When `href` is set, the value renders as a link. */
export interface QuoteDetailItem {
  label: string;
  value: string;
  href?: string | null;
}

interface QuoteDetailListProps {
  items: QuoteDetailItem[];
}

/**
 * Read-only label/value grid used for the INSTALLATION DETAILS section, showing
 * the configuration the customer submitted. Empty values are skipped.
 */
const QuoteDetailList: React.FC<QuoteDetailListProps> = ({ items }) => {
  const theme = useTheme();

  const visibleItems = items.filter((item) => item.value || item.href);
  if (!visibleItems.length) {
    return null;
  }

  const gridStyle: React.CSSProperties = {
    display: "grid",
    // One column on narrow screens, two on the full-width document — the 280px
    // floor is what stops a third column appearing. `min()` keeps the track
    // from overflowing containers narrower than that.
    gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 280px), 1fr))",
    columnGap: theme.spacing(3),
    rowGap: theme.spacing(1),
  };

  const labelStyle: React.CSSProperties = {
    fontWeight: 600,
    marginBottom: theme.spacing(0.25),
  };

  return (
    <div style={gridStyle}>
      {visibleItems.map((item) => (
        <div key={item.label}>
          <Typography variant="body2" style={labelStyle}>
            {item.label}
          </Typography>

          {item.href ? (
            <a
              href={item.href}
              target="_blank"
              rel="noopener noreferrer"
              style={{
                color: theme.palette.primary.main,
                fontSize: theme.typography.body2.fontSize,
              }}
            >
              {item.value}
            </a>
          ) : (
            <Typography variant="body2">{item.value}</Typography>
          )}
        </div>
      ))}
    </div>
  );
};

export default QuoteDetailList;
